/**
 * The on-screen touch controls: the movement stick and the interact button.
 *
 * The stick is drawn by mutating DOM style directly from the `stickView` channel rather
 * than through React state — see `stickView.ts` for why. React renders this component
 * once; a thumb drag never re-renders it.
 */

import { useEffect, useRef } from 'react';
import { actionLabel, availableAction } from '../game/interaction';
import { canStow } from '../game/pocket';
import type { PlacedObject } from '../game/profile/buildGeometry';
import { useGameStore } from '../store/gameStore';
import { inputState } from './inputState';
import { STICK_RADIUS_PX } from './producers/touch';
import { subscribeStick } from './stickView';

const BASE_SIZE = STICK_RADIUS_PX * 2;
const KNOB_SIZE = STICK_RADIUS_PX;

export function TouchControls() {
  const baseRef = useRef<HTMLDivElement>(null);
  const knobRef = useRef<HTMLDivElement>(null);

  useEffect(
    () =>
      subscribeStick((stick) => {
        const base = baseRef.current;
        const knob = knobRef.current;
        if (!base || !knob) return;

        if (!stick) {
          base.style.opacity = '0';
          knob.style.transform = 'translate(-50%, -50%)';
          return;
        }

        const dx = stick.current.x - stick.origin.x;
        const dy = stick.current.y - stick.origin.y;
        const distance = Math.hypot(dx, dy);
        const scale = distance > STICK_RADIUS_PX ? STICK_RADIUS_PX / distance : 1;

        base.style.opacity = '1';
        base.style.left = `${stick.origin.x}px`;
        base.style.top = `${stick.origin.y}px`;
        knob.style.transform = `translate(calc(-50% + ${dx * scale}px), calc(-50% + ${dy * scale}px))`;
      }),
    [],
  );

  return (
    <div className="touch-controls" aria-hidden="true">
      <div
        ref={baseRef}
        className="touch-controls__stick"
        style={{
          position: 'fixed',
          width: BASE_SIZE,
          height: BASE_SIZE,
          marginLeft: -BASE_SIZE / 2,
          marginTop: -BASE_SIZE / 2,
          borderRadius: '50%',
          border: '2px solid rgba(255, 255, 255, 0.55)',
          background: 'rgba(0, 0, 0, 0.25)',
          opacity: 0,
          transition: 'opacity 120ms ease-out',
          pointerEvents: 'none',
        }}
      >
        <div
          ref={knobRef}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: KNOB_SIZE,
            height: KNOB_SIZE,
            marginLeft: 0,
            marginTop: 0,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.75)',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    </div>
  );
}

/**
 * The interact affordance for touch: appears only when there is something to do, and
 * says what — "Open", "Pick up", "Drop". Separate from the stick overlay because it is
 * a real control — focusable, labelled, and reachable by keyboard — while the stick is
 * decoration for a gesture and is hidden from assistive technology.
 *
 * `data-hud` is what stops a touch on this button from also spawning a movement stick.
 * Sizing (min 64px, past the 44px accessibility floor) comes from `.hud-button`.
 */
export function ContextActionButton() {
  const focus = useGameStore((store) => store.focus);
  const carriedObjectId = useGameStore((store) => store.carriedObjectId);
  const carriedObjectKind = useGameStore((store) => store.carriedObjectKind);
  const doorStates = useGameStore((store) => store.doorStates);
  const roomLights = useGameStore((store) => store.roomLights);
  const attachments = useGameStore((store) => store.attachments);

  const action = availableAction(focus, {
    carriedObjectId,
    carriedObjectKind,
    doorStates,
    roomLights,
    attachments,
  });
  if (!action) return null;

  return (
    <button
      type="button"
      data-hud
      className="hud-button hud-button--interact"
      onPointerDown={() => {
        inputState.interact = true;
        inputState.interactHeld = true;
      }}
      // Three ways a hold can end, and only the first is the happy one: the finger lifts,
      // the browser cancels the touch to run a gesture of its own, or the finger slides
      // off the button and takes its pointerup with it. Miss any of them and the table
      // keeps being dragged by a finger that is no longer there.
      onPointerUp={() => {
        inputState.interactHeld = false;
      }}
      onPointerCancel={() => {
        inputState.interactHeld = false;
      }}
      onPointerLeave={() => {
        inputState.interactHeld = false;
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') inputState.interact = true;
      }}
    >
      {actionLabel(action)}
    </button>
  );
}

/** Always available during play — jumping needs no target, unlike interacting. */
export function JumpButton() {
  return (
    <button
      type="button"
      data-hud
      className="hud-button hud-button--jump"
      onPointerDown={() => {
        inputState.jump = true;
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') inputState.jump = true;
      }}
    >
      Jump
    </button>
  );
}

/**
 * Shown only while carrying, beside the contextual button, so drop (interact) and
 * throw are two distinct thumb targets — mirroring the two bindings on desktop.
 */
export function ThrowButton() {
  const carriedObjectId = useGameStore((store) => store.carriedObjectId);
  if (carriedObjectId === null) return null;

  return (
    <button
      type="button"
      data-hud
      className="hud-button hud-button--throw"
      onPointerDown={() => {
        inputState.throw = true;
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') inputState.throw = true;
      }}
    >
      Throw
    </button>
  );
}

/**
 * The way into the pause overlay on a touch device, where there is no pointer lock to
 * lose. Everything that overlay holds — settings, look sensitivity, the build id, and the
 * restart action that takes a waiting update — is reachable only through this button.
 *
 * It dispatches to the store directly rather than through `InputState`. Pause is not
 * player input: it is a shell event about whether there is a frame loop at all, and
 * routing it through the per-frame state would mean writing the store from inside
 * `useFrame`. `onClick` rather than `onPointerDown` for the same reason the label is a
 * word and not an icon — pausing is deliberate, a graze that slides off should not stop
 * the game, and a click brings Enter and Space with it for free.
 */
export function PauseButton() {
  const dispatch = useGameStore((store) => store.dispatch);

  return (
    <button
      type="button"
      data-hud
      className="hud-button hud-button--pause"
      onClick={() => {
        dispatch('PAUSE');
      }}
    >
      Pause
    </button>
  );
}

/**
 * The touch stow control, shown only while stowing would actually do something: a small
 * item in the hands and a free slot to put it in. A button that is always there and
 * usually refuses teaches the player to ignore it.
 *
 * It takes the level's objects because that is where a held id becomes a kind — the store
 * holds the id and nothing else, deliberately, so it never carries a copy of the level's
 * contents (design D4).
 */
export function StowButton({ objects }: { objects: readonly PlacedObject[] }) {
  const carriedObjectId = useGameStore((store) => store.carriedObjectId);
  const pocket = useGameStore((store) => store.pocket);
  const profile = useGameStore((store) => store.profile);

  const kind = objects.find((object) => object.id === carriedObjectId)?.kind;
  // The whole condition, the game's pocket included — a button in a game with no pocket
  // would press into a store that refuses (`per-game-pocket` D3, D4).
  if (!canStow(kind ?? null, pocket, profile)) return null;

  return (
    <button
      type="button"
      data-hud
      className="hud-button hud-button--stow"
      onPointerDown={() => {
        inputState.stow = true;
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') inputState.stow = true;
      }}
    >
      Pocket
    </button>
  );
}
